import { ApolloError } from "@apollo/client";
import { Formik, FormikProps } from "formik";
import React, { useEffect, useState } from "react";
import {
  FormStepLayout,
  StepProps,
} from "src/components/Layout/FormStepLayout";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import {
  FormWithAnswersTracker,
  useFormContext,
  useVisibilityContext,
} from "src/components/Providers/FormProvider";
import { usePutAnswerForMe } from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useFormSteps } from "src/hooks/useFormSteps";
import { useGlossary } from "src/hooks/useGlossary";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import useRankedSchools from "src/hooks/useSchoolRank";
import { getContext, saveContext } from "src/scenes/parent/state/localStorage";
import { useEligibleSchools } from "src/services/eligibility/useEligibleSchools";
import { triggerRuleExecutionsForSoftDeletedOffers } from "src/services/form/offer";
import { Answer, Question } from "src/services/formTemplate";
import * as AFF from "src/services/formTemplateFilters";
import { validateFormTemplate } from "src/services/formTemplateValidations";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import * as RemoteData from "src/types/remoteData";
import { QuestionList } from "./QuestionList";
import { ExploreSavedSchoolsDialog } from "./components/Dialogs/ExploreSavedSchools";
import { FormButtons } from "./components/Layout/FormButtons";
import { INSERT_FORM_SCHOOLS_RANK } from "./graphql/mutations";
import { useAutosaveReducer } from "./hooks/useAutosaveReducer";
import {
  HooksFn,
  useEligibilityAnswersChange,
} from "./hooks/useEligibilityAnswersChange";
import { useRemoveAnswersForHiddenQuestions } from "./hooks/useRemoveAnswersForHiddenQuestions";
import { rankedSchoolsVar } from "./store";

function getMaxSchools(
  allSections: AF.Sections<AF.WithId>
): number | undefined {
  const section = allSections.find(
    (item) => item?.type === AF.SchoolRankingSectionType
  );
  return section
    ? (section as AF.SchoolRankingSection<AF.WithId>).maxSchools
    : undefined;
}

type Props = {
  formTemplateId: string;
  answers: GQL.GetFormAnswersById;
  formId: uuid;
  applicant: AFF.Types.Applicant;
  refetchSchoolRanks: () => Promise<unknown>;
  previousFormSchoolIds: uuid[];
  schoolRanksRemoteData: RemoteData.RemoteData<ApolloError, GQL.GetSchoolsRank>;
  section: AF.PreRankingSection<AF.WithId>;
  allSections: AF.Sections<AF.WithId>;
  verificationResults: GQL.FormFragment_form_verification_results[];
  hasBeenSubmittedBefore: boolean | null;
  enrollmentPeriodId: string;
} & StepProps;

type PreRankingContainerProps = {
  formId: uuid;
  applicant: AFF.Types.Applicant;
  formikProps: FormikProps<any>;
  questions: AF.Question<AF.WithId>[];
  verificationResults: GQL.FormFragment_form_verification_results[];
  previousFormSchoolIds: uuid[];
  onAutosave: (questionId: uuid, saving: boolean) => void;
  afterAutosave: () => Promise<void>;
  eligibilityChange: ReturnType<HooksFn>;
  confirmationDialog: React.ReactNode;
  hasBeenSubmittedBefore: boolean | null;
  setNumVisibleQuestions: (numVisibleQuestions: number) => void;
};

/**
 * This in-between container component helps to trigger validation on mount
 * and display error messages when applicable.
 */
const StepPreRankingContainer: React.FC<PreRankingContainerProps> = ({
  formId,
  applicant,
  formikProps,
  questions,
  verificationResults,
  previousFormSchoolIds,
  onAutosave,
  afterAutosave,
  eligibilityChange,
  confirmationDialog,
  hasBeenSubmittedBefore,
  setNumVisibleQuestions,
}) => {
  const visibilityContext = useVisibilityContext();
  useEffect(() => {
    if (!hasBeenSubmittedBefore) {
      return;
    }
    formikProps.validateForm().then((errors) => {
      Object.keys(errors).forEach((key) => {
        formikProps.setFieldTouched(key);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions]);

  const completeQuestions = Question.getCompleteQuestions(questions);
  const applicableQuestions = Question.groupByVerifications(
    Question.getCompleteApplicableQuestions(
      questions,
      formikProps.values,
      {
        rankedSchoolIds: [],
        previousFormSchoolIds,
      },
      visibilityContext
    )
  );

  useEffect(() => {
    setNumVisibleQuestions(applicableQuestions.length);
  }, [applicableQuestions, setNumVisibleQuestions]);

  return (
    <>
      <QuestionList
        formId={formId}
        applicant={applicant}
        completeQuestions={completeQuestions}
        applicableQuestions={applicableQuestions}
        formikProps={formikProps}
        verificationResults={verificationResults}
        confirmGradesChange={eligibilityChange.confirmGrades}
        confirmEligibilityChange={eligibilityChange.confirmEligibilityQuestion}
        confirmAddressChange={eligibilityChange.confirmAddress}
        onAutosave={onAutosave}
        afterAutosave={afterAutosave}
      />
      {confirmationDialog}
      {eligibilityChange.dialog}
    </>
  );
};

export const StepPreRanking: React.FC<Props> = (props) => {
  const {
    formTemplateId,
    answers,
    formId,
    applicant,
    refetchSchoolRanks,
    previousFormSchoolIds,
    schoolRanksRemoteData,
    section,
    allSections,
    verificationResults,
    hasBeenSubmittedBefore,
    enrollmentPeriodId,

    ...stepProps
  } = props;
  const { glossary } = useGlossary();
  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: glossary`Edit family/student information`,
    body: null,
    cancelButton: { label: "No, cancel" },
    confirmButton: { label: "Yes, continue" },
    translate: true,
  });

  const [numVisibleQuestions, setNumVisibleQuestions] = useState(0);

  const schoolRankingSection = React.useMemo(
    () =>
      allSections.find(
        (section) => section?.type === "SchoolRankingSection"
      ) as AF.SchoolRankingSection<AF.WithId> | undefined,
    [allSections]
  );

  const rankingEnabled = React.useMemo(
    () => schoolRankingSection?.rankingEnabled ?? false,
    [schoolRankingSection]
  );

  const [
    insertSchoolRank,
    {
      remoteData: insertSchoolRanksRemoteData,
      reset: resetInsertSchoolRanksRemoteData,
    },
  ] = useRemoteDataMutation<
    GQL.InsertFormSchoolsRank,
    GQL.InsertFormSchoolsRankVariables
  >(INSERT_FORM_SCHOOLS_RANK);

  const { mutateAsync: putAnswerForMe } = usePutAnswerForMe(undefined, {
    onSuccess: (data) => {
      if ("error" in data) throw new Error(data.error?.error.message);
    },
  });

  const { getDeletedRanks, getDeletedOffers, getDeletedWaitlists } =
    useRankedSchools([]);

  const flags = useFlags(["form-service-form-answers"]);

  const toast = useAvelaToast();
  React.useEffect(() => {
    if (
      insertSchoolRanksRemoteData.kind === RemoteData.RemoteDataKind.Success
    ) {
      resetInsertSchoolRanksRemoteData();
    }
  }, [insertSchoolRanksRemoteData, resetInsertSchoolRanksRemoteData, toast]);

  const { autosaveStatus, onAutosave } = useAutosaveReducer();

  const { remoteData: eligibleSchoolsRD, refetch: refetchEligibleSchools } =
    useEligibleSchools({
      formTemplateId,
      formId,
      enrollmentPeriodId,
      preRankingSection: section,
      selectedSchools: [],
    });

  const hasExploreSavedSchools = React.useMemo(() => {
    const ctx = getContext();
    if (!ctx) return false;
    if (ctx.exploreSavedSchools.length === 0) return false;
    return true;
  }, []);

  const handleExploreSavedSchools = React.useCallback(
    async (_: Answer.FormikValues): Promise<boolean> => {
      const ctx = getContext();
      if (!ctx) return true;
      if (!ctx.exploreSavedSchools.length) return true;
      if (
        schoolRanksRemoteData.hasData() &&
        schoolRanksRemoteData.data.form_school_rank.length > 0
      ) {
        // already have school selection, don't overwrite the existing selection with saved schools from explore
        return true;
      }

      if (!eligibleSchoolsRD.hasData()) {
        // This shouldn't happen, but just in case, we should fall back of to ignoring explore saved school
        console.error(
          "Missing eligible schools data when checking eligibility for explore saved schools"
        );
        return true;
      }

      if (!schoolRankingSection?.id) return true;

      setIsLoading(true);
      const rankedSchools = rankedSchoolsVar();

      const allEligibleSchools = ctx.exploreSavedSchools.filter((item) =>
        eligibleSchoolsRD.data.find((s) => s.id === item.id)
      );

      const ineligibleSchools = ctx.exploreSavedSchools.filter(
        (item) => !allEligibleSchools.map((item) => item.id).includes(item.id)
      );

      const maxSchools = getMaxSchools(allSections) ?? 0;
      const remainingSchoolCount = maxSchools - rankedSchools.length;

      const eligibleSchools = maxSchools
        ? allEligibleSchools.slice(0, remainingSchoolCount)
        : allEligibleSchools;
      const removedSchools = maxSchools
        ? allEligibleSchools.slice(maxSchools, undefined)
        : [];

      const cancelLabel =
        !eligibleSchools.length && ineligibleSchools.length ? "Cancel" : "No";
      const confirmLabel =
        !eligibleSchools.length && ineligibleSchools.length
          ? "Continue"
          : glossary`Yes, add schools`;
      const isConfirmed = await confirm({
        header: glossary`School choices`,
        body: (
          <ExploreSavedSchoolsDialog
            eligibleSchools={eligibleSchools}
            ineligibleSchools={ineligibleSchools}
            removedSchools={removedSchools}
            numUnresolvedSchools={ctx.unresolvedReferenceIDs.length}
          ></ExploreSavedSchoolsDialog>
        ),
        cancelButton: { label: cancelLabel },
        confirmButton: { label: confirmLabel },
      });

      if (isConfirmed) {
        try {
          const upsertedSchoolRanks = eligibleSchools.map((school, index) => {
            return {
              form_id: formId,
              schools_ranking_section_id: schoolRankingSection.id,
              school_id: school.id,
              rank: index,
            };
          });
          const deletedSchoolRanks = getDeletedRanks(upsertedSchoolRanks);
          const deletedOffers = getDeletedOffers(upsertedSchoolRanks);
          const deletedWaitlists = getDeletedWaitlists(upsertedSchoolRanks);
          if (flags["form-service-form-answers"].enabled) {
            await putAnswerForMe({
              path: {
                formId: formId,
                questionId: schoolRankingSection.id,
              },
              body: {
                data: upsertedSchoolRanks.map((rank) => ({
                  schoolId: rank.school_id,
                  rank: rank.rank,
                })),
              },
            });
          } else {
            const result = await insertSchoolRank({
              variables: {
                deleted_school_ranks: deletedSchoolRanks,
                delete_offers_where: deletedOffers,
                delete_waitlists_where: deletedWaitlists,
                upserted_school_ranks: upsertedSchoolRanks,
              },
            });
            await triggerRuleExecutionsForSoftDeletedOffers({
              offers: result.data?.delete_offer?.returning ?? [],
              scope: "me",
            });
          }
          saveContext(null);
          await refetchSchoolRanks();
        } catch (e) {
          console.error(e);
          toast.error({
            title: "Something went wrong!",
            description: "Check your network and try again",
          });
        }
      }

      return isConfirmed;
    },
    [
      schoolRanksRemoteData,
      eligibleSchoolsRD,
      schoolRankingSection,
      allSections,
      glossary,
      confirm,
      getDeletedRanks,
      getDeletedOffers,
      getDeletedWaitlists,
      flags,
      refetchSchoolRanks,
      formId,
      putAnswerForMe,
      insertSchoolRank,
      toast,
    ]
  );
  const { onNext } = useFormSteps(stepProps);
  const [isLoading, setIsLoading] = React.useState(false);

  const removeAnswersForHiddenQuestions = useRemoveAnswersForHiddenQuestions({
    formId,
  });
  const formContext = useFormContext();
  const handleSubmit = async (answers: Answer.FormikValues) => {
    if (autosaveStatus === "Saving") {
      return;
    }

    await removeAnswersForHiddenQuestions();
    await formContext.refetchAnswers();
    await refetchEligibleSchools();

    await handleExploreSavedSchools(answers);
    onNext();
  };

  const initialValues = Answer.getFormikInitialValues(
    section.questions,
    answers.form_answer,
    answers.grades_answer,
    answers.form_address,
    answers.custom_question_answer
  );

  const eligibilityChange = useEligibilityAnswersChange({
    formId,
    formTemplateId,
    refetchSchoolRanks,
    schoolRankingSection,
    schoolRanksRemoteData,
    rankingEnabled,
  });

  const visibilityContext = useVisibilityContext();

  const afterAutosave = React.useCallback(async () => {
    if (hasExploreSavedSchools) {
      await refetchEligibleSchools();
    }
  }, [hasExploreSavedSchools, refetchEligibleSchools]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validateFormTemplate(
        section.questions,
        {
          rankedSchoolIds: [],
          previousFormSchoolIds,
        },
        visibilityContext
      )}
      validateOnBlur={true}
      validateOnMount={false} // need to disable this since it's causing weird validation behavior since we introduce refetchAnswer. See: https://app.asana.com/0/0/1203247877978830/1203832069318649/f
    >
      {(formikProps) => (
        <FormStepLayout
          noValidate
          as={FormWithAnswersTracker}
          title={section.title}
          description={section.description}
          buttons={(buttonsProps) => (
            <FormButtons
              saveStatus={autosaveStatus}
              {...buttonsProps}
              overridePreviousButton={{
                hide: true,
              }}
              overrideNextButton={{
                disabled: !formikProps.isValid,
                isLoading: isLoading || formikProps.isSubmitting,
                action: () => {
                  // Do nothing here, and instead, next button will trigger form submission
                },
              }}
              hasBeenSubmittedBefore={hasBeenSubmittedBefore ?? false}
              sectionType={section.type}
              stepQuestionAmount={numVisibleQuestions}
            />
          )}
          {...stepProps}
          content={
            <StepPreRankingContainer
              formId={formId}
              applicant={applicant}
              formikProps={formikProps}
              questions={section.questions}
              verificationResults={verificationResults}
              previousFormSchoolIds={previousFormSchoolIds}
              onAutosave={onAutosave}
              eligibilityChange={eligibilityChange}
              confirmationDialog={confirmationDialog}
              hasBeenSubmittedBefore={hasBeenSubmittedBefore}
              setNumVisibleQuestions={setNumVisibleQuestions}
              afterAutosave={afterAutosave}
            />
          }
        />
      )}
    </Formik>
  );
};
